/**
 * requestGateDecision — put a governed act in front of a human and return what they decided.
 *
 * Framework-free on purpose: it works in the twelve plain-HTML mari8x surfaces, in React, and in
 * anything widget-forge eventually renders into. It renders and resolves; it does NOT call
 * gate-proof itself, because capturing a proof is the caller's transaction and this file must not
 * decide when a signature is written.
 *
 *   const d = await requestGateDecision({
 *     capability: 'publish-corpus-entry',
 *     what: 'Publish 18 corpus entries',
 *     consequence: '<b>18 entries</b> become publicly readable and citable.',
 *     irreversible: 'Published entries cannot be withdrawn once indexed.',
 *     widgetOutput: {...},          // THE DOCUMENT — what is being signed
 *     user: { id:'L-9F2', role:'author' },
 *     requireReason: true,
 *   });
 *   // → { decision:'approved'|'rejected', reason, at }  — then the CALLER captures the proof
 *
 * See pause.css for the five rules this implements. The two that are easiest to break by accident:
 * nothing is autofocused, and reject is exactly as easy as approve.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** The output is what gets signed, so it is shown whole — and when it cannot be, that is DECLARED. */
const MAX_SHOWN = 4000;
function renderOutput(out) {
  const text = typeof out === 'string' ? out : JSON.stringify(out, null, 2);
  if (text.length <= MAX_SHOWN) return { body: esc(text), truncated: 0 };
  return { body: esc(text.slice(0, MAX_SHOWN)), truncated: text.length - MAX_SHOWN };
}

export function requestGateDecision(opts = {}) {
  const {
    capability = '', what = 'Confirm this action', consequence = '', irreversible = '',
    widgetOutput = null, user = {}, requireReason = false, surface = location.pathname,
  } = opts;

  return new Promise((resolve) => {
    const { body, truncated } = renderOutput(widgetOutput);

    const scrim = document.createElement('div');
    scrim.className = 'gp-scrim';
    scrim.setAttribute('role', 'dialog');
    scrim.setAttribute('aria-modal', 'true');
    scrim.setAttribute('aria-label', 'A decision is required');
    scrim.innerHTML = `
      <div class="gp">
        <div class="gp-head">
          <div class="gp-eyebrow">Held — your decision</div>
          <div class="gp-what">${esc(what)}</div>
          ${consequence ? `<div class="gp-consequence">${consequence}</div>` : ''}
          ${irreversible ? `<div class="gp-irrev"><span aria-hidden="true">⚠</span>
            <span>${esc(irreversible)}</span></div>` : ''}
        </div>
        <div class="gp-doc">
          <div class="g-label gp-doc-label">What you are signing</div>
          <pre class="gp-out">${body}</pre>
          ${truncated ? `<div class="gp-truncated">${truncated.toLocaleString()} more characters are
            not shown. <b>You would be signing what you cannot see</b> — reject this and ask for the
            act to be split.</div>` : ''}
        </div>
        <div class="gp-foot">
          ${requireReason ? `<input class="g-input gp-why" id="gp-why"
             placeholder="Why? (recorded with your decision)" autocomplete="off">` : ''}
          <div class="gp-acts">
            <button class="g-btn gp-reject" data-d="rejected">Reject</button>
            <button class="g-btn gp-approve" data-d="approved">Approve</button>
          </div>
          <div class="gp-who">Recorded as <b>${esc(user.id || 'unidentified')}</b>${
            user.role ? ` · ${esc(user.role)}` : ''} · signed and hash-chained${
            capability ? ` · <span class="g-mono">${esc(capability)}</span>` : ''}</div>
        </div>
      </div>`;

    document.body.appendChild(scrim);
    requestAnimationFrame(() => scrim.classList.add('on'));

    /* NOTHING IS AUTOFOCUSED. Focus goes to the dialog itself so a keyboard user is placed inside it
       and can tab to a choice — but no choice is pre-selected and Enter does not resolve anything.
       A person tapping through by reflex has to actually pick. */
    const gp = scrim.querySelector('.gp');
    gp.setAttribute('tabindex', '-1');
    gp.focus({ preventScroll: true });

    // Focus is trapped: a modal a keyboard user can tab out of is a modal that can be answered
    // without being seen.
    const focusable = () => [...scrim.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])')];
    scrim.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); finish('rejected'); return; }  // Esc = reject, never approve
      if (e.key !== 'Tab') return;
      const f = focusable(); if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    let done = false;
    function finish(decision) {
      if (done) return;
      const why = scrim.querySelector('#gp-why');
      const reason = why ? why.value.trim() : '';
      // A required reason is required for BOTH outcomes. Requiring it only to reject is how a system
      // makes refusing more expensive than approving, which is rule 2 broken by paperwork.
      if (requireReason && !reason) {
        if (why) { why.style.borderColor = 'var(--g-null)'; why.focus(); }
        return;
      }
      done = true;
      scrim.classList.remove('on');
      setTimeout(() => scrim.remove(), 240);
      resolve({ decision, reason, at: new Date().toISOString(), capability, surface,
                userId: user.id || null, role: user.role || null, widgetOutput });
    }

    scrim.querySelectorAll('.gp-acts button').forEach((b) => b.onclick = () => finish(b.dataset.d));
    // The scrim is NOT click-to-dismiss. An accidental tap outside must not resolve a governed turn.
  });
}

/**
 * The receipt. Shown after the caller has captured the proof — because a system whose promise is
 * that every human decision is provable, and which never shows anyone a proof, is asking to be
 * taken on trust. That is the opposite of the claim.
 */
export function showGateReceipt({ decision, hash, proof_id, ledger } = {}) {
  const scrim = document.createElement('div');
  scrim.className = 'gp-scrim';
  const approved = decision === 'approved';
  scrim.innerHTML = `
    <div class="gp"><div class="gp-receipt">
      <div class="mark" style="color:${approved ? 'var(--g-ok)' : 'var(--g-ink-3)'}">${approved ? '✓' : '—'}</div>
      <div class="g-h3">${approved ? 'Approved and recorded' : 'Rejected and recorded'}</div>
      <p class="g-body" style="margin-top:var(--g-2)">${approved
        ? 'The act has been authorised in your name.'
        : 'Nothing was done. Your rejection is on record, which is a result rather than an absence.'}</p>
      ${hash ? `<div class="gp-hash"><b>proof</b> ${esc(proof_id || '')}<br><b>hash</b> ${esc(hash)}${
        ledger ? `<br><b>ledger</b> ${esc(ledger)}` : ''}</div>` : ''}
      <button class="g-btn" style="margin-top:var(--g-4);width:100%">Close</button>
    </div></div>`;
  document.body.appendChild(scrim);
  requestAnimationFrame(() => scrim.classList.add('on'));
  scrim.querySelector('button').onclick = () => {
    scrim.classList.remove('on'); setTimeout(() => scrim.remove(), 240);
  };
}
